import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useId, useState } from 'react'
import {
  CONSENT_ACTIONS_CLASS,
  CONSENT_HIDDEN_TRANSLATE,
  CONSENT_POSITION_CLASS,
  CONSENT_PRIMARY_CLASS,
  CONSENT_RULE_CLASS,
  CONSENT_SECONDARY_CLASS,
  CONSENT_SHEET_CLASS,
  CONSENT_TITLE_CLASS
} from './consentChrome'

export type IPreferences = {
  analytics: boolean
  advertisement: boolean
  functionality: boolean
  essential: boolean // Always enabled
}

type IProps = {
  onSave: (preferences: IPreferences) => void
  onRejectAll: () => void
  onClose: () => void
}

type ToggleSwitchProps = {
  checked: boolean
  onChange: () => void
  disabled?: boolean
  label: string
}

type OptionalCategory = 'analytics' | 'advertisement' | 'functionality'

/**
 * The button is a 44×56 tap target; the visible 24×44 track sits inside it
 * and carries the focus ring. Track colours clear the 3:1 non-text contrast
 * minimum on white: blue-800 when on (6.96:1), ink-400/70 when off (3.4:1).
 */
const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  disabled,
  label
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={onChange}
    className={`group -mr-1.5 inline-flex h-11 w-14 shrink-0 items-center justify-center focus-visible:outline-none ${
      disabled ? 'cursor-not-allowed' : 'cursor-pointer'
    }`}
  >
    <span
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 group-focus-visible:outline-2 group-focus-visible:outline-offset-2 group-focus-visible:outline-ink-700 motion-reduce:transition-none ${
        checked ? 'bg-blue-800' : 'bg-ink-400/70'
      } ${disabled ? 'opacity-60' : ''}`}
    >
      <span
        className={`inline-block size-4 rounded-full bg-white-500 transition-transform duration-200 motion-reduce:transition-none ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </span>
  </button>
)

const ManageCookiesModal: React.FC<IProps> = ({
  onSave,
  onRejectAll,
  onClose
}) => {
  const { t } = useTranslation('common')
  const [isAnimating, setIsAnimating] = useState(false)
  const [preferences, setPreferences] = useState<IPreferences>({
    analytics: true,
    advertisement: true,
    functionality: true,
    essential: true
  })
  const titleId = useId()
  const descriptionId = useId()

  // Literal keys only — a `t(\`COOKIES.${x}\`)` template is a type error under
  // the typed resources, so each category spells its keys out.
  const cookieCategories: Array<{
    key: keyof IPreferences
    title: string
    description: string
    disabled?: boolean
  }> = [
    {
      key: 'essential',
      title: t('COOKIES.CATEGORY_ESSENTIAL_TITLE'),
      description: t('COOKIES.CATEGORY_ESSENTIAL_DESC'),
      disabled: true
    },
    {
      key: 'analytics',
      title: t('COOKIES.CATEGORY_ANALYTICS_TITLE'),
      description: t('COOKIES.CATEGORY_ANALYTICS_DESC')
    },
    {
      key: 'advertisement',
      title: t('COOKIES.CATEGORY_ADVERTISEMENT_TITLE'),
      description: t('COOKIES.CATEGORY_ADVERTISEMENT_DESC')
    },
    {
      key: 'functionality',
      title: t('COOKIES.CATEGORY_FUNCTIONALITY_TITLE'),
      description: t('COOKIES.CATEGORY_FUNCTIONALITY_DESC')
    }
  ]

  useEffect(() => {
    requestAnimationFrame(() => setIsAnimating(true))
  }, [])

  const handleToggle = (type: OptionalCategory) => {
    setPreferences((prev) => ({ ...prev, [type]: !prev[type] }))
  }

  const handleClose = useCallback(() => {
    setIsAnimating(false)
    setTimeout(onClose, 300)
  }, [onClose])

  useEffect(() => {
    if (!isAnimating) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isAnimating, handleClose])

  return (
    <>
      {/* Backdrop — closes the preferences sheet back to the banner. It never
          records a choice, unlike the banner's old accept-on-tap backdrop.
          Plain ink tint, no backdrop-filter: a full-screen blur is costly on
          phone GPUs (design-language.md → banned patterns). */}
      <div
        className={`fixed inset-0 z-9998 bg-ink-800/30 transition-opacity duration-300 motion-reduce:transition-none md:hidden ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Modal — same footprint and chrome as the banner it replaces. */}
      <div
        role="dialog"
        aria-modal="false"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={`${CONSENT_POSITION_CLASS} transition-[transform,opacity] duration-300 motion-reduce:transition-none ${
          isAnimating
            ? 'translate-y-0 opacity-100'
            : `${CONSENT_HIDDEN_TRANSLATE} opacity-0`
        }`}
      >
        <div className={CONSENT_SHEET_CLASS}>
          <div className={CONSENT_RULE_CLASS} aria-hidden="true" />

          {/* Header */}
          <div className="px-4 pt-4 md:px-6 md:pt-6">
            <h3
              id={titleId}
              className={`text-[20px] md:text-[22px] ${CONSENT_TITLE_CLASS}`}
            >
              {t('COOKIES.MANAGE_TITLE')}
            </h3>
            <p
              id={descriptionId}
              className="mt-1.5 text-[13px] leading-snug text-ink-200 md:text-sm"
            >
              {t('COOKIES.MANAGE_SUBTITLE')}
            </p>
          </div>

          {/* Cookie categories — hairline rows, the toggle is the only control */}
          <div className="mt-3 max-h-[40vh] overflow-y-auto px-4 md:mt-4 md:px-6">
            <ul className="divide-y divide-ink-100/15 border-y border-ink-100/15">
              {cookieCategories.map((category) => (
                <li
                  key={category.key}
                  className="flex items-center justify-between gap-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink-700">
                      {category.title}
                      {category.disabled && (
                        <span className="ml-2 text-xs font-normal text-ink-200">
                          {t('COOKIES.ALWAYS_ON')}
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-xs leading-snug text-ink-200">
                      {category.description}
                    </p>
                  </div>
                  <ToggleSwitch
                    checked={preferences[category.key]}
                    onChange={() =>
                      !category.disabled &&
                      handleToggle(category.key as OptionalCategory)
                    }
                    disabled={category.disabled}
                    label={t('COOKIES.TOGGLE_LABEL', {
                      category: category.title
                    })}
                  />
                </li>
              ))}
            </ul>
          </div>

          {/* Actions */}
          <div
            className={`px-4 pt-3 md:px-6 md:pt-5 md:pb-6 ${CONSENT_ACTIONS_CLASS}`}
          >
            <button
              type="button"
              onClick={() => onSave({ ...preferences, essential: true })}
              className={CONSENT_PRIMARY_CLASS}
            >
              {t('COOKIES.SAVE_PREFERENCES')}
            </button>
            <button
              type="button"
              onClick={onRejectAll}
              className={CONSENT_SECONDARY_CLASS}
            >
              {t('COOKIES.REJECT_ALL')}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default ManageCookiesModal
